import { CommunityId, formatDateNoTime, nullEntityId, RegionId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useRegionId } from "../../../store/selectors/hooks";
import { trpc } from "../../rpc/trpc";
import { Badge } from "../../ui/badge";
import { ButtonSelect } from "../../ui/button-select";
import { DangerButton } from "../../ui/danger-button";
import { CommunityLink } from "../../ui/entity-link";
import { ErrorMessage } from "../../ui/error-message";
import { fancyConfirm } from "../../ui/fancy-confirm";
import { PrimaryButton } from "../../ui/primary-button";
import { SearchInputDebounced } from "../../ui/search-input-debounced";
import { SecondaryButton } from "../../ui/secondary-button";
import { Spinner } from "../../ui/spinner";

export interface MergeCandidate {
    id: CommunityId;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    updatedAt?: string;
    updatedByName?: string;
}

type MergeMatchedField = "location" | "evergreenCommunityId";

const matchedFieldLabels: Record<MergeMatchedField, string> = {
    location: "Same location",
    evergreenCommunityId: "Same Evergreen ID",
};

const candidateLocation = (candidate: MergeCandidate): string =>
    [candidate.address, candidate.city, candidate.state].filter(Boolean).join(", ");

const lastUpdatedLabel = (candidate: MergeCandidate): string | null =>
    candidate.updatedAt && candidate.updatedByName
        ? `Last updated ${formatDateNoTime(candidate.updatedAt)} by ${candidate.updatedByName}`
        : null;

const CandidateMeta: React.FunctionComponent<{
    candidate: MergeCandidate;
}> = props => {
    const { candidate } = props;
    const updated = lastUpdatedLabel(candidate);
    return (
        <>
            <p className="text-sm text-gray-500">{candidateLocation(candidate)}</p>
            {updated ? <p className="text-xs text-gray-400">{updated}</p> : null}
        </>
    );
};

const CandidateDetails: React.FunctionComponent<{
    candidate: MergeCandidate;
}> = props => {
    const { candidate } = props;
    return (
        <div>
            <p className="font-medium">{candidate.name}</p>
            <CandidateMeta candidate={candidate} />
        </div>
    );
};

const countLabel = (key: string): string => key.replace(/([A-Z])/g, " $1").toLowerCase();

interface CommunityPickerProps {
    label: string;
    regionId: RegionId | null;
    excludeId: CommunityId | null;
    selected: MergeCandidate | null;
    onSelect: (candidate: MergeCandidate) => void;
    onClear: () => void;
}

const CommunityPicker: React.FunctionComponent<CommunityPickerProps> = props => {
    const { label, regionId, excludeId, selected, onSelect, onClear } = props;
    const trpcClient = trpc.useTRPC();
    const [query, setQuery] = useState("");
    const searchQuery = useQuery({
        ...trpcClient.communityMerge.searchCandidates.queryOptions({
            query,
            regionId: regionId ?? nullEntityId<RegionId>(),
        }),
        enabled: query.trim() !== "" && regionId !== null,
    });

    if (selected) {
        const updated = lastUpdatedLabel(selected);
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <p className="text-sm font-medium text-gray-500">{label}</p>
                <p className="text-lg font-semibold">{selected.name}</p>
                <p className="text-sm text-gray-500">{candidateLocation(selected)}</p>
                {updated ? <p className="text-xs text-gray-400">{updated}</p> : null}
                <div className="mt-4">
                    <SecondaryButton type="button" onClick={onClear}>
                        Change
                    </SecondaryButton>
                </div>
            </div>
        );
    }

    const candidates = (searchQuery.data ?? []).filter(
        candidate => candidate.id !== excludeId,
    );

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <SearchInputDebounced
                initial=""
                onChange={setQuery}
                label={label}
                placeholder="Search communities by name"
            />
            <ul className="mt-4 divide-y divide-gray-200">
                {candidates.map(candidate => (
                    <li
                        key={candidate.id}
                        className="py-2 flex items-center justify-between">
                        <CandidateDetails candidate={candidate} />
                        <SecondaryButton
                            type="button"
                            onClick={() => onSelect(candidate)}>
                            Select
                        </SecondaryButton>
                    </li>
                ))}
                {searchQuery.data && candidates.length === 0 ? (
                    <li className="py-2 text-sm text-gray-500">No matches</li>
                ) : null}
            </ul>
        </div>
    );
};

interface DuplicateSuggestionsProps {
    communityId: CommunityId;
    onSelect: (candidate: MergeCandidate) => void;
}

const DuplicateSuggestions: React.FunctionComponent<
    DuplicateSuggestionsProps
> = props => {
    const { communityId, onSelect } = props;
    const trpcClient = trpc.useTRPC();
    const duplicatesQuery = useQuery(
        trpcClient.communityMerge.duplicateCandidates.queryOptions({ communityId }),
    );
    const duplicates = duplicatesQuery.data ?? [];
    if (duplicates.length === 0) {
        return null;
    }
    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h5 className="text-sm font-semibold">Possible duplicates</h5>
            <p className="text-sm text-gray-600">
                These communities share the survivor&apos;s location or Evergreen link.
            </p>
            <ul className="mt-2 divide-y divide-gray-200">
                {duplicates.map(candidate => (
                    <li
                        key={candidate.id}
                        className="py-2 flex items-center justify-between">
                        <div>
                            <CommunityLink
                                communityId={candidate.id}
                                name={candidate.name}
                                className="font-medium"
                            />
                            <CandidateMeta candidate={candidate} />
                            <div className="mt-1 flex space-x-2">
                                {candidate.matchedFields.map(field => (
                                    <Badge
                                        key={field}
                                        type="highlight"
                                        value={matchedFieldLabels[field]}
                                    />
                                ))}
                            </div>
                        </div>
                        <SecondaryButton
                            type="button"
                            onClick={() => onSelect(candidate)}>
                            Select
                        </SecondaryButton>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const CountsTable: React.FunctionComponent<{
    counts: Record<string, number>;
}> = props => {
    const entries = Object.entries(props.counts).filter(([, value]) => value > 0);
    if (entries.length === 0) {
        return (
            <p className="text-sm text-gray-500">
                No related records reference the community being merged away.
            </p>
        );
    }
    return (
        <table className="min-w-full text-sm">
            <tbody>
                {entries.map(([key, value]) => (
                    <tr key={key} className="border-b border-gray-100">
                        <td className="py-1 pr-8 capitalize">{countLabel(key)}</td>
                        <td className="py-1 text-right font-medium">{value}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

interface MergeConfirmationProps {
    survivor: MergeCandidate;
    loser: MergeCandidate;
    onReset: () => void;
}

type MergeChoice = "survivor" | "loser";

type MergeFieldValue = string | number | null | undefined;

const fieldValueLabel = (value: MergeFieldValue): string =>
    value === null || value === undefined ? "(blank)" : String(value);

interface FieldDiffRowProps {
    field: string;
    survivorValue: MergeFieldValue;
    loserValue: MergeFieldValue;
    choice: MergeChoice;
    onChoose: (choice: MergeChoice) => void;
}

const FieldDiffRow: React.FunctionComponent<FieldDiffRowProps> = props => {
    const { field, survivorValue, loserValue, choice, onChoose } = props;
    return (
        <div className="py-2 border-b border-gray-100">
            <p className="text-sm font-medium capitalize">{countLabel(field)}</p>
            <ButtonSelect
                options={[
                    {
                        id: "survivor",
                        label: `Keep ${fieldValueLabel(survivorValue)}`,
                    },
                    { id: "loser", label: `Use ${fieldValueLabel(loserValue)}` },
                ]}
                selectedIds={[choice]}
                onToggle={id => onChoose(id === "loser" ? "loser" : "survivor")}
            />
        </div>
    );
};

const MergeConfirmation: React.FunctionComponent<MergeConfirmationProps> = props => {
    const { survivor, loser, onReset } = props;
    const trpcClient = trpc.useTRPC();
    const [fieldChoiceOverrides, setFieldChoiceOverrides] = useState<
        Record<string, MergeChoice>
    >({});
    const executeMutation = useMutation(
        trpcClient.communityMerge.execute.mutationOptions(),
    );
    const previewQuery = useQuery({
        ...trpcClient.communityMerge.preview.queryOptions({
            survivorId: survivor.id,
            loserId: loser.id,
        }),
        enabled: !executeMutation.isSuccess,
    });

    if (executeMutation.isSuccess) {
        const counts = executeMutation.data.repointCounts;
        const total = Object.values(counts).reduce((sum, value) => sum + value, 0);
        return (
            <div className="bg-white shadow rounded-lg p-6 space-y-4">
                <h2 className="text-lg font-semibold">Merge complete</h2>
                <p>
                    {loser.name} was merged into {survivor.name}. {total} related
                    record(s) were moved.
                </p>
                <CountsTable counts={counts} />
                <PrimaryButton type="button" onClick={onReset}>
                    Merge more communities
                </PrimaryButton>
            </div>
        );
    }

    if (previewQuery.isLoading) {
        return <Spinner />;
    }
    if (previewQuery.error) {
        return (
            <ErrorMessage component="CommunityMergePanel">
                <pre>{previewQuery.error.message}</pre>
            </ErrorMessage>
        );
    }
    if (!previewQuery.data) {
        return null;
    }
    const preview = previewQuery.data;

    const choiceFor = (diff: (typeof preview.fieldDiffs)[number]): MergeChoice =>
        fieldChoiceOverrides[diff.field] ??
        (diff.survivorValue === null || diff.survivorValue === undefined
            ? "loser"
            : "survivor");

    const buildFieldChoices = () => {
        const fieldChoices: Partial<
            Record<(typeof preview.fieldDiffs)[number]["field"], MergeChoice>
        > = {};
        for (const diff of preview.fieldDiffs) {
            fieldChoices[diff.field] = choiceFor(diff);
        }
        return fieldChoices;
    };

    const confirmMerge = async () => {
        const confirmed = await fancyConfirm(
            "Merge communities",
            `This will merge ${loser.name} into ${survivor.name}. You can undo it afterward from the "Recent merges" list.`,
            "Merge communities",
            "Cancel",
            { confirmStyle: "alert", extraConfirm: true },
        );
        if (confirmed) {
            executeMutation.mutate({
                survivorId: survivor.id,
                loserId: loser.id,
                fieldChoices: buildFieldChoices(),
            });
        }
    };

    return (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold">Preview</h2>
            <p>
                Merging <span className="font-medium">{preview.loserName}</span> into{" "}
                <span className="font-medium">{preview.survivorName}</span>. Records to
                move:
            </p>
            <CountsTable counts={preview.repointCounts} />
            {preview.answerConflicts > 0 ? (
                <p className="text-sm text-amber-700">
                    {preview.answerConflicts} question answer(s) exist on both
                    communities; the survivor&apos;s answers will be kept.
                </p>
            ) : null}
            {preview.quickBooksRealmConflicts.length > 0 ? (
                <p className="text-sm text-amber-700">
                    Both communities have QuickBooks customers for realm(s){" "}
                    {preview.quickBooksRealmConflicts.join(", ")}; the survivor&apos;s
                    customer will be kept.
                </p>
            ) : null}
            {preview.willAdoptEvergreenCommunityId ? (
                <p className="text-sm text-gray-600">
                    The survivor will adopt the merged community&apos;s Evergreen link.
                </p>
            ) : null}
            {preview.willAdoptCommunityMasterId ? (
                <p className="text-sm text-gray-600">
                    The survivor will adopt the merged community&apos;s public-records
                    match.
                </p>
            ) : null}
            {preview.willAdoptReferralTier ? (
                <p className="text-sm text-gray-600">
                    The survivor will adopt the merged community&apos;s referral tier.
                </p>
            ) : null}
            {preview.fieldDiffs.length > 0 ? (
                <div>
                    <h3 className="text-sm font-semibold">Profile field differences</h3>
                    <p className="text-sm text-gray-600">
                        Choose which value the surviving community keeps for each field.
                    </p>
                    <div className="mt-2">
                        {preview.fieldDiffs.map(diff => (
                            <FieldDiffRow
                                key={diff.field}
                                field={diff.field}
                                survivorValue={diff.survivorValue}
                                loserValue={diff.loserValue}
                                choice={choiceFor(diff)}
                                onChoose={choice =>
                                    setFieldChoiceOverrides(current => ({
                                        ...current,
                                        [diff.field]: choice,
                                    }))
                                }
                            />
                        ))}
                    </div>
                </div>
            ) : null}
            {executeMutation.error ? (
                <p className="text-sm text-red-700">{executeMutation.error.message}</p>
            ) : null}
            <DangerButton
                type="button"
                disabled={executeMutation.isPending}
                onClick={confirmMerge}>
                Merge communities
            </DangerButton>
        </div>
    );
};

const RecentMerges: React.FunctionComponent<{
    communityId: CommunityId;
}> = props => {
    const { communityId } = props;
    const trpcClient = trpc.useTRPC();
    const recentQuery = useQuery(
        trpcClient.communityMerge.recentMerges.queryOptions({ communityId }),
    );
    const undoMutation = useMutation({
        ...trpcClient.communityMerge.undo.mutationOptions(),
        onSuccess: () => {
            recentQuery.refetch();
        },
    });
    const merges = recentQuery.data ?? [];
    if (merges.length === 0) {
        return null;
    }

    const confirmUndo = async (merge: (typeof merges)[number]) => {
        const confirmed = await fancyConfirm(
            "Undo merge",
            `This will restore ${merge.loserName} as a separate community and move its records back off this community.`,
            "Undo merge",
            "Cancel",
            { confirmStyle: "alert" },
        );
        if (confirmed) {
            undoMutation.mutate({ mergeId: merge.mergeId });
        }
    };

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h5 className="text-sm font-semibold">Recent merges into this community</h5>
            <ul className="mt-2 divide-y divide-gray-200">
                {merges.map(merge => (
                    <li
                        key={merge.mergeId}
                        className="py-2 flex items-center justify-between">
                        <div>
                            <p className="font-medium">{merge.loserName}</p>
                            <p className="text-xs text-gray-400">
                                Merged {formatDateNoTime(merge.createdAt)} by{" "}
                                {merge.mergedByName}
                            </p>
                            {merge.undoneAt ? (
                                <p className="text-xs text-gray-400">
                                    Undone {formatDateNoTime(merge.undoneAt)}
                                </p>
                            ) : null}
                        </div>
                        {merge.reversible ? (
                            <SecondaryButton
                                type="button"
                                disabled={undoMutation.isPending}
                                onClick={() => confirmUndo(merge)}>
                                Undo
                            </SecondaryButton>
                        ) : (
                            <span className="text-xs text-gray-400">
                                {merge.undoneAt ? "Undone" : "Cannot undo"}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
            {undoMutation.error ? (
                <p className="mt-2 text-sm text-red-700">{undoMutation.error.message}</p>
            ) : null}
        </div>
    );
};

interface CommunityMergePanelProps {
    community: MergeCandidate;
}

export const CommunityMergePanel: React.FunctionComponent<
    CommunityMergePanelProps
> = props => {
    const { community } = props;
    const regionId = useRegionId("community");
    const [survivor, setSurvivor] = useState<MergeCandidate | null>(community);
    const [loser, setLoser] = useState<MergeCandidate | null>(null);

    const reset = () => {
        setSurvivor(community);
        setLoser(null);
    };

    return (
        <div className="space-y-6">
            <h4 className="text-lg">Merge communities</h4>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <CommunityPicker
                    label="Survivor (kept)"
                    regionId={regionId}
                    selected={survivor}
                    excludeId={loser?.id ?? null}
                    onSelect={setSurvivor}
                    onClear={() => setSurvivor(null)}
                />
                <CommunityPicker
                    label="Community to merge away"
                    regionId={regionId}
                    selected={loser}
                    excludeId={survivor?.id ?? null}
                    onSelect={setLoser}
                    onClear={() => setLoser(null)}
                />
            </div>
            {survivor && !loser ? (
                <DuplicateSuggestions communityId={survivor.id} onSelect={setLoser} />
            ) : null}
            {survivor && loser ? (
                <MergeConfirmation
                    key={`${survivor.id}-${loser.id}`}
                    survivor={survivor}
                    loser={loser}
                    onReset={reset}
                />
            ) : null}
            <RecentMerges communityId={community.id} />
        </div>
    );
};
